/**
 * CompanyLayout
 *
 * Cross-scope shell for /company/* pages. Mirrors the structure used by
 * AppLayout for /location/* — a full-width AppHeader on top, sidebar
 * below, main content area with an ocean band on the left edge as the
 * scope-distinct visual signal.
 *
 *   ┌────────────── full-width AppHeader ──────────────┐
 *   │  brand · getaway(company) · scope · bell · user  │
 *   ├──────┬────────────────────────────────────────────┤
 *   │ side │ ▌  main content (ocean band on left edge)  │
 *   │ bar  │ ▌                                          │
 *   └──────┴────────────────────────────────────────────┘
 *
 * AppHeader reads `useActiveContext()` and adapts itself: GetawaySwitcher
 * shows the active company; POSLauncherButton + NfcStatusIndicator are
 * suppressed (no POS / NFC infrastructure at the company tier). The
 * ocean band on the main content is the only company-only visual cue —
 * everything above and beside is shared with location.
 */

import { SidebarProvider } from '@/theme/components/ui/sidebar';
import { CompanyRailSidebar } from '@/components/sidebar/CompanyRailSidebar';
import { useTenant } from '@/core/tenant/TenantContext';
import { NfcCardScanPopover } from '@/shared/components/NfcCardScanPopover';
import { MobileDrawerAutoClose } from '@/components/MobileDrawerAutoClose';
import { AppHeader } from '@/components/AppHeader';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { useBrandCSSVars } from '@/theme/brandColors';

// Mirror AppLayout's cookie-driven default-open behavior so the user's
// collapsed/expanded preference survives navigation between Company
// pages (each ProtectedPage wraps in a fresh CompanyLayout, so without
// reading the cookie back we'd snap to defaultOpen=true on every link).
const SIDEBAR_COOKIE_NAME = 'sidebar:state';

function readSidebarOpenCookie(): boolean {
  if (typeof document === 'undefined') return true;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${SIDEBAR_COOKIE_NAME}=`));
  if (!match) return true;
  return match.split('=')[1] === 'true';
}

interface CompanyLayoutProps {
  children: React.ReactNode;
}

export function CompanyLayout({ children }: CompanyLayoutProps) {
  const { companyBrandColor } = useTenant();
  const brandVars = useBrandCSSVars(companyBrandColor);

  return (
    <SidebarProvider
      defaultOpen={readSidebarOpenCookie()}
      style={
        {
          ...brandVars,
          '--sidebar-width': '19rem',
          '--sidebar-width-icon': '4rem',
        } as React.CSSProperties
      }
    >
      <MobileDrawerAutoClose />
      {/* h-svh + overflow-hidden makes <main> the scroll container —
          mirror of the AppLayout fix so the sticky AppHeader stays
          anchored when content scrolls past the viewport. */}
      <div className="h-svh w-full flex flex-col overflow-hidden">
        {/* Mirror AppLayout: ImpersonationBanner spans full width above
            the header so it's just as visible from company-scope as
            from location-scope. Without this the operator can lose
            track that the session is impersonated while browsing
            /company/* surfaces. */}
        <ImpersonationBanner />
        <AppHeader />
        <div className="flex flex-1 min-h-0 w-full">
          <CompanyRailSidebar />
          {/* Company-scope visual signal — vertical ocean band on the left
              edge of the content area. Mirrors the page-frame.company::before
              element in shaka-deck-mockup-example-scope-staff.html so a user
              opening a company-scope page recognises the scope instantly,
              without reading the topbar pill. Location pages don't carry the
              band — calmer is the default operating mode. */}
          <main role="main" className="relative flex-1 bg-card pl-[5px] overflow-y-auto overflow-x-hidden">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 left-0 w-[5px] bg-gradient-to-b from-ocean-700 via-ocean-500 to-ocean-700"
            />
            {children}
          </main>
        </div>
        <NfcCardScanPopover />
      </div>
    </SidebarProvider>
  );
}
